import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    return {
        catalogPageState,
        category: getCategoryState(state, ownProps.categoryId).value,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasCategoryContext;

export interface CategoryDetailSubCategoriesStyles {
    container?: InjectableCss;
    title?: InjectableCss;
    titleWrapper?: InjectableCss;
    subCategoryItem?: InjectableCss;
    subCategoryLink?: LinkPresentationProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        css: css`
            border: 4px solid ${COLORS.background.grayShade};
            background: ${COLORS.white};
            width: 100%;
            height: 100%;
            padding: 24px;
            ul {
                margin-top: 24px !important;
                li {
                    list-style: none;
                    display: inline;
                    &:after {
                        content: " • ";
                        color: ${COLORS.background.tag};
                    }
                    &:last-child:after {
                        content: none;
                    }
                }
            }
        `,
    },
    titleWrapper: {
        css: css`
            background: linear-gradient(0deg, rgba(38, 107, 64, 0.08), rgba(38, 107, 64, 0.08)), #ffffff;
            border-radius: 40px;
            width: 196px;
            min-height: 32px;
            padding: 4px 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        width: 215px;
                    `,
                    null,
                    css`
                        width: max-content;
                    `,
                    null,
                ])}
        `,
    },
    title: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            width: 164px;
            height: 24px;
            color: ${COLORS.primary.dark};
        `,
    },
    subCategoryItem: {
        css: css`
            display: inline;
        `,
    },
    subCategoryLink: {
        css: css`
            span {
                color: ${getProp("theme.typography.p.color")} !important;
                text-decoration-line: underline;
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.tiny};
                line-height: 16px;
            }
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CENCategoryDetailSubCategories = ({ category, catalogPageState }: Props) => {
    const subCategories =
        catalogPageState?.value?.properties?.subCategories &&
        JSON.parse(decodeURI(catalogPageState?.value.properties.subCategories));

    if (!category || !subCategories || subCategories.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>{translate("Browse by Category")}</Typography>
            </StyledWrapper>
            <ul>
                {subCategories.map((subCategory: any) => (
                    <li key={subCategory.id}>
                        <StyledWrapper {...styles.subCategoryItem}>
                            <Link href={`${category.path}/${subCategory.urlSegment}`} {...styles.subCategoryLink}>
                                {subCategory.name}
                            </Link>
                        </StyledWrapper>
                    </li>
                ))}
            </ul>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CENCategoryDetailSubCategories)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
